import { Link, useLocation } from 'react-router-dom'
import { isNavActive, NAV_ITEMS } from '@/config/nav-items'
import { cn } from '@/lib/utils'
import type { AppNavMode } from '@/hooks/useDeviceLayout'

interface AppSidebarProps {
  mode: AppNavMode
  className?: string
}

export function AppSidebar({ mode, className }: AppSidebarProps) {
  const location = useLocation()
  const compact = mode === 'sidebar-compact'

  return (
    <aside
      className={cn(
        'flex shrink-0 flex-col border-r border-border bg-card',
        compact ? 'w-[4.5rem]' : 'w-56',
        className,
      )}
      data-testid="app-sidebar"
      aria-label="Main navigation"
    >
      <div className={cn('border-b border-border', compact ? 'px-2 py-3' : 'px-4 py-4')}>
        <p className="text-xs font-medium uppercase tracking-wide text-primary">ModelScout</p>
        {!compact ? (
          <p className="text-sm font-semibold text-foreground">AI Lab</p>
        ) : (
          <p className="sr-only">ModelScout AI Lab</p>
        )}
      </div>

      <nav className="flex flex-1 flex-col gap-1 p-2" data-testid="app-nav">
        {NAV_ITEMS.map((item) => {
          const active = isNavActive(location.pathname, item.to)
          const Icon = item.icon

          return (
            <Link
              key={item.to}
              to={item.to}
              title={item.label}
              className={cn(
                'flex min-h-11 items-center rounded-md text-sm transition-colors',
                compact ? 'flex-col justify-center gap-0.5 px-1 py-2 text-[10px]' : 'gap-3 px-3 py-2',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
              data-testid={item.testId}
            >
              <Icon className={cn('shrink-0', compact ? 'h-5 w-5' : 'h-4 w-4')} aria-hidden />
              <span className={cn(compact ? 'leading-none' : 'font-medium')}>
                {compact ? item.shortLabel : item.label}
              </span>
            </Link>
          )
        })}
      </nav>
    </aside>
  )
}
